// Safety screens (PMU-237), shared by Messages and Lost and found: the community rules to accept
// before writing, the report form, and the office's list of reports.
import { useEffect, useState } from "react";
import { Badge, Banner, Button, Dialog, DialogClose, Select, Spinner, TextField } from "@vavr/ui";
import { api } from "@vavr/screens/shared";
import "./safety.css";

interface Terms { version: string; rules: string[]; accepted: boolean; reasons: { value: string; label: string }[] }
let terms: Terms | null = null;
const subs = new Set<() => void>();

/** The community rules and whether this person accepted them (one fetch for the app). */
export function useTerms(): [Terms | null, () => Promise<void>] {
  const [, bump] = useState(0);
  useEffect(() => {
    const f = () => bump((x) => x + 1);
    subs.add(f);
    if (!terms) void api<Terms>("GET", "/api/v1/safety/terms").then((r) => { terms = r; subs.forEach((s) => s()); }, () => undefined);
    return () => { subs.delete(f); };
  }, []);
  const accept = async () => { await api("POST", "/api/v1/safety/terms"); terms = terms ? { ...terms, accepted: true } : terms; subs.forEach((s) => s()); };
  return [terms, accept];
}

/** Shown instead of the text box until the rules are accepted. */
export function RulesCard({ compact }: { compact?: boolean }) {
  const [t, accept] = useTerms();
  const [busy, setBusy] = useState(false);
  if (!t) return <Spinner label="Loading" />;
  return (
    <div className={`psf-rules${compact ? " psf-rules--compact" : ""}`}>
      <b>Before you write: our community rules</b>
      <ul>{t.rules.map((r) => <li key={r}>{r}</li>)}</ul>
      <div className="psf-rules__acts">
        <a href="https://vavr.us/terms#community-rules" target="_blank" rel="noopener noreferrer">Full terms</a>
        <Button loading={busy} onClick={async () => { setBusy(true); try { await accept(); } finally { setBusy(false); } }}>I agree</Button>
      </div>
    </div>
  );
}

/** The report form: a reason from the list and an optional note. `path` is the endpoint; `extra` goes with it (the message). */
export function ReportDialog({ path, extra, what, trigger, onDone }: { path: string; extra?: Record<string, unknown>; what: string; trigger: React.ReactElement; onDone?: () => void }) {
  const [t] = useTerms();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    setBusy(true); setError(null);
    try { await api("POST", path, { ...extra, reason, note: note || undefined }); setDone(true); onDone?.(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <Dialog title={done ? "Thank you" : `Report ${what}`} description={done ? undefined : "The school's leaders (and Vavr) look at every report. The person is not told who reported it."}
      open={open} onOpenChange={(v) => { setOpen(v); if (v) { setReason(""); setNote(""); setDone(false); setError(null); } }} trigger={trigger}
      actions={done ? <DialogClose><Button>Close</Button></DialogClose> : <><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={busy} disabled={!reason} onClick={() => void send()}>Report</Button></>}>
      {done ? <p>We have passed it on. If a child is in danger, call the school or 911 now.</p> : (
        <div className="pmu-stack">
          {error && <Banner tone="danger" title={error} />}
          <Select label="What is wrong" value={reason} placeholder="Choose a reason" onValueChange={(v) => setReason(v ?? "")} options={(t?.reasons ?? []).map((r) => ({ value: r.value, label: r.label }))} />
          <TextField label="Anything to add (optional)" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      )}
    </Dialog>
  );
}

interface Report {
  id: string; kind: "message" | "lostfound" | "user"; target_id: string; thread_id: string | null; reason: string; reason_label: string; note: string | null; excerpt: string | null;
  by_name: string; reported_name: string | null; status: "open" | "resolved"; resolution: string | null; resolved_by_name: string | null; created_at: number;
}

/** The office: reports about this school's conversations and items, to look at and close. */
export function ReportsView({ siteId, tz, openThread }: { siteId: string; tz?: string; openThread?: (id: string) => void }) {
  const [list, setList] = useState<Report[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => api<{ reports: Report[] }>("GET", `/api/v1/sites/${siteId}/safety/reports`).then((r) => setList(r.reports), (e: Error) => setError(e.message));
  useEffect(() => { void load(); }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = async (r: Report) => {
    const resolution = window.prompt("What did you do? (kept with the report)", "Reviewed") ?? "";
    if (!resolution) return;
    try { await api("POST", `/api/v1/sites/${siteId}/safety/reports/${r.id}`, { resolution }); await load(); } catch (e) { setError((e as Error).message); }
  };
  if (!list) return error ? <Banner tone="danger" title={error} /> : <Spinner label="Loading" />;
  const at = (ts: number) => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz }).format(ts);
  return (
    <section className="pmu-stack">
      <h2 className="psf-head">Reports</h2>
      {error && <Banner tone="danger" title={error} />}
      {!list.length ? <p className="pmu-muted">No reports. Anyone can report a message or a lost-and-found item; you are emailed, and it appears here.</p> : (
        <ul className="psf-list">
          {list.map((r) => (
            <li key={r.id} className="psf-item" data-open={r.status === "open" || undefined}>
              <div className="psf-item__top">
                <Badge tone={r.status === "open" ? (r.reason === "child_safety" ? "danger" : "warning") : "neutral"}>{r.reason_label}</Badge>
                <span className="pmu-small pmu-muted">{r.kind === "message" ? "Message" : r.kind === "lostfound" ? "Lost and found" : "Person"}{r.reported_name ? ` from ${r.reported_name}` : ""} · reported by {r.by_name} · {at(r.created_at)}</span>
              </div>
              {r.excerpt && <p className="psf-item__excerpt">{r.excerpt}</p>}
              {r.note && <p className="pmu-small">"{r.note}"</p>}
              <div className="psf-item__acts">
                {r.thread_id && openThread && <Button size="sm" variant="secondary" onClick={() => openThread(r.thread_id!)}>Open the conversation</Button>}
                {r.status === "open" ? <Button size="sm" onClick={() => void close(r)}>Close the report</Button> : <span className="pmu-small pmu-muted">Closed by {r.resolved_by_name}: {r.resolution}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
